import type { PermissionDomain } from '@oy/types';
import { isEqual } from 'lodash-es';
import { useCallback } from 'react';
import { checkPermission } from 'src/utilities/permission';
import { useAppSelector } from '../useAppSelector';

export const usePermission = (
  domain = 'BUSINESS_DASHBOARD' as PermissionDomain,
) => {
  const { permissionProducts = [], domainPermission } = useAppSelector(
    state => ({
      permissionProducts: state.permission.permissionProducts,
      domainPermission: state.permission.userPermissions.find(
        permission => permission.domain === domain,
      ),
    }),
    isEqual,
  );

  const hasPermission = useCallback(
    (productName: string, permissionDetail: string) => {
      if (!domainPermission) return false;

      // Find and check for product with given `domain` and `productName`
      const requiredProduct = permissionProducts.find(
        permissionProduct =>
          permissionProduct.domain === domain &&
          permissionProduct.product === productName,
      );

      if (!requiredProduct) return false;

      // Find and check for required permission detail on the `requiredProduct`
      const requiredPermissionDetail = requiredProduct.permission_details.find(
        detail => detail.permission_detail === permissionDetail,
      );
      if (!requiredPermissionDetail) return false;

      // Find and check for current logged in user permission product based on the `productName`
      const userPermissionProduct = domainPermission.permission_details?.find(
        detail => detail.product === productName,
      );
      if (!userPermissionProduct) return false;

      return checkPermission(
        userPermissionProduct.permission_value,
        requiredPermissionDetail.permission_integer,
      );
    },
    [domain, permissionProducts, domainPermission],
  );

  return { hasPermission };
};
